'use client';

/**
 * Nine slide templates, extracted from the Telus Spark pitch deck and made
 * data-driven. Each template accepts a typed slide + optional edit callbacks
 * so the same component powers both the editor canvas and the final PDF
 * render.
 */

import QRCode from 'react-qr-code';
import type {
  AskSlide,
  BlockSlide,
  CardsSlide,
  CoverSlide,
  PricingSlide,
  ProblemsSlide,
  QrSlide,
  RightColumnSlide,
  Slide,
  TranscriptSlide,
  TwoColumnSlide,
} from '@/lib/deck/types';
import type { Block } from '@/lib/deck/blocks';
import { patchSlideBlock } from '@/lib/deck/block-helpers';
import { BlockSlideView } from './BlockSlide';
import { EditableText } from './EditableText';
import { SlideFrame } from './SlideFrame';
import {
  ACCENT,
  BADGE,
  BODY,
  BODY_DIM,
  BODY_LG,
  BODY_SM,
  CAPTION,
  CARD,
  CARD_TITLE,
  DECK_BULLET_GLYPH,
  DECK_BULLET_LIST,
  DECK_BULLET_LIST_TIGHT,
  DECK_BULLET_MARKER,
  DECK_BULLET_ROW,
  DECK_BULLET_TEXT,
  EYEBROW,
  EYEBROW_SM,
  H1,
  H1_COVER,
  H2,
  INNER,
  LEDE,
  META,
  PAD,
  PRICE,
  QUOTE_SM,
} from './tokens';

type Patch<T> = (patch: Partial<T>) => void;

interface TemplateRenderProps<T> {
  slide: T;
  onChange?: Patch<T>;
  onImageClick?: () => void;
}

function textTone(color?: 'light' | 'dark') {
  const light = color !== 'dark';
  return {
    eyebrow: light ? 'text-white/60' : 'text-[#2E3245]/55',
    headline: light ? 'text-white' : 'text-[#2E3245]',
    lede: light ? 'text-white/80' : 'text-[#2E3245]/75',
    body: light ? 'text-white/80' : 'text-[#2E3245]/80',
    bodyDim: light ? 'text-white/75' : 'text-[#2E3245]/60',
    caption: light ? 'text-white/55' : 'text-[#2E3245]/55',
    accent: light ? 'text-white' : 'text-[#2E3245]',
  };
}

function ImageLayer({ onClick }: { onClick?: () => void }) {
  if (!onClick) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Change background image"
      className="deck-image-slot absolute inset-0 z-0 cursor-pointer"
    />
  );
}

// ── 1. Cover ───────────────────────────────────────────────────────────────
export function CoverTemplate({ slide, onChange, onImageClick }: TemplateRenderProps<CoverSlide>) {
  const t = textTone(slide.textColor ?? 'light');
  return (
    <SlideFrame id={slide.id} bg={slide.bg} bgColor={slide.bg?.color}>
      <ImageLayer onClick={onImageClick} />
      <div className={`relative z-10 flex min-h-screen flex-col ${PAD} ${INNER}`}>
        <div className="flex flex-shrink-0 items-center justify-start gap-4">
          {slide.logoSrc && (
            <img src={slide.logoSrc} alt="" className="h-[30px] w-auto" />
          )}
        </div>

        <div className="flex flex-1 flex-col justify-center">
          <div className="cover-copy mr-auto max-w-3xl">
            <EditableText
              as="p"
              className={`${EYEBROW} mb-6 ${t.eyebrow}`}
              value={slide.eyebrow}
              onChange={onChange ? (v) => onChange({ eyebrow: v }) : undefined}
            />
            <EditableText
              as="h1"
              multiline
              className={`${H1_COVER} mb-6 max-w-3xl ${t.headline}`}
              value={slide.headline}
              onChange={onChange ? (v) => onChange({ headline: v }) : undefined}
            />
            {(slide.subheadline || onChange) && (
              <EditableText
                as="p"
                multiline
                className={`${BODY_LG} mb-0 max-w-2xl ${t.body}`}
                value={slide.subheadline ?? ''}
                onChange={onChange ? (v) => onChange({ subheadline: v }) : undefined}
                placeholder="Optional subheadline"
              />
            )}
          </div>
        </div>

        <div className="mt-8 flex max-w-3xl flex-shrink-0 flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            {slide.bylineAvatar && (
              <img
                src={slide.bylineAvatar}
                alt=""
                className="h-10 w-10 rounded-full object-cover ring-2 ring-white/20"
              />
            )}
            <div>
              {(slide.bylinePrefix || onChange) && (
                <EditableText
                  as="p"
                  className={`${CAPTION} mb-0.5 font-semibold tracking-widest ${t.caption}`}
                  value={slide.bylinePrefix ?? ''}
                  onChange={onChange ? (v) => onChange({ bylinePrefix: v }) : undefined}
                  placeholder="Prepared by"
                />
              )}
              <EditableText
                as="p"
                className={`font-inter text-base font-semibold ${t.body}`}
                value={slide.bylineName ?? ''}
                onChange={onChange ? (v) => onChange({ bylineName: v }) : undefined}
                placeholder="Name"
              />
            </div>
          </div>
          {(slide.footnote || onChange) && (
            <EditableText
              as="p"
              className={`${CAPTION} tracking-widest ${t.caption}`}
              value={slide.footnote ?? ''}
              onChange={onChange ? (v) => onChange({ footnote: v }) : undefined}
              placeholder="Footer note"
            />
          )}
        </div>
      </div>
    </SlideFrame>
  );
}

// ── 2. Right-column headline + lede ────────────────────────────────────────
export function RightColumnTemplate({
  slide,
  onChange,
  onImageClick,
}: TemplateRenderProps<RightColumnSlide>) {
  const t = textTone(slide.textColor ?? 'light');
  const alignRight = (slide.align ?? 'right') === 'right';
  return (
    <SlideFrame id={slide.id} bg={slide.bg} bgColor={slide.bg?.color}>
      <ImageLayer onClick={onImageClick} />
      <div className={`relative z-10 flex min-h-screen flex-col justify-center ${PAD} ${INNER}`}>
        <div className={`w-full max-w-md md:max-w-[50%] ${alignRight ? 'md:ml-auto' : ''}`}>
          <EditableText
            as="p"
            className={`mb-3 ${EYEBROW} ${t.eyebrow}`}
            value={slide.eyebrow}
            onChange={onChange ? (v) => onChange({ eyebrow: v }) : undefined}
          />
          <EditableText
            as="h2"
            multiline
            className={`${H1} ${t.headline}`}
            value={slide.headline}
            onChange={onChange ? (v) => onChange({ headline: v }) : undefined}
          />
          <EditableText
            as="p"
            multiline
            className={`${LEDE} mt-3 ${t.lede}`}
            value={slide.lede}
            onChange={onChange ? (v) => onChange({ lede: v }) : undefined}
          />
          {(slide.footnote || onChange) && (
            <EditableText
              as="p"
              className={`${CAPTION} mt-10 font-bold tracking-[0.14em] ${t.caption}`}
              value={slide.footnote ?? ''}
              onChange={onChange ? (v) => onChange({ footnote: v }) : undefined}
              placeholder="Footer note"
            />
          )}
        </div>
      </div>
    </SlideFrame>
  );
}

// ── 3. Transcript ──────────────────────────────────────────────────────────
export function TranscriptTemplate({
  slide,
  onChange,
  onImageClick,
}: TemplateRenderProps<TranscriptSlide>) {
  const t = textTone(slide.textColor ?? 'dark');
  const updateMessage = (i: number, patch: Partial<TranscriptSlide['messages'][number]>) => {
    if (!onChange) return;
    const next = slide.messages.map((m, j) => (i === j ? { ...m, ...patch } : m));
    onChange({ messages: next });
  };
  return (
    <SlideFrame id={slide.id} bg={slide.bg} bgColor={slide.bg?.color}>
      <ImageLayer onClick={onImageClick} />
      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-[min(100%,1400px)] items-center px-4 py-10 sm:px-6 sm:py-12 md:px-10 md:py-14">
        <div className="grid w-full grid-cols-1 items-stretch gap-8 lg:grid-cols-[1fr_1.8fr_0.85fr] lg:items-center lg:gap-8">
          <div className="max-w-md">
            <EditableText
              as="p"
              className={`mb-3 ${EYEBROW} ${t.eyebrow}`}
              value={slide.eyebrow}
              onChange={onChange ? (v) => onChange({ eyebrow: v }) : undefined}
            />
            <EditableText
              as="h2"
              multiline
              className={`${H1} ${t.headline}`}
              value={slide.headline}
              onChange={onChange ? (v) => onChange({ headline: v }) : undefined}
            />
            {(slide.lede || onChange) && (
              <EditableText
                as="p"
                multiline
                className={`${LEDE} mt-3 ${t.lede}`}
                value={slide.lede ?? ''}
                onChange={onChange ? (v) => onChange({ lede: v }) : undefined}
                placeholder="Optional lede"
              />
            )}
          </div>
          <div className="hidden min-h-[12rem] lg:block" aria-hidden />
          <div className={`${CARD} p-5 md:p-6`}>
            <div className="space-y-3">
              {slide.messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <EditableText
                    as="div"
                    multiline
                    className={`${BODY_SM} max-w-[86%] rounded-2xl px-4 py-2.5 ${
                      msg.sender === 'user'
                        ? 'bg-[#2E3245] text-white'
                        : 'bg-marine-green text-white'
                    }`}
                    value={msg.text}
                    onChange={onChange ? (v) => updateMessage(i, { text: v }) : undefined}
                  />
                </div>
              ))}
            </div>
            {(slide.meta || onChange) && (
              <EditableText
                as="p"
                className={`${META} mt-6 border-t border-[#2E3245]/10 pt-4 text-[#2E3245]/50`}
                value={slide.meta ?? ''}
                onChange={onChange ? (v) => onChange({ meta: v }) : undefined}
                placeholder="Meta line"
              />
            )}
          </div>
        </div>
      </div>
    </SlideFrame>
  );
}

// ── 4. Cards (announcements-style) ─────────────────────────────────────────
export function CardsTemplate({ slide, onChange, onImageClick }: TemplateRenderProps<CardsSlide>) {
  const t = textTone(slide.textColor ?? 'light');
  const alignRight = (slide.align ?? 'right') === 'right';
  const updateCard = (i: number, patch: Partial<CardsSlide['cards'][number]>) => {
    if (!onChange) return;
    const next = slide.cards.map((c, j) => (i === j ? { ...c, ...patch } : c));
    onChange({ cards: next });
  };
  return (
    <SlideFrame id={slide.id} bg={slide.bg} bgColor={slide.bg?.color}>
      <ImageLayer onClick={onImageClick} />
      <div
        className={`relative z-10 flex min-h-screen items-center ${alignRight ? 'justify-end' : ''} ${PAD} ${INNER}`}
      >
        <div className="w-full max-w-[min(62%,920px)] space-y-8">
          <header>
            <EditableText
              as="p"
              className={`mb-3 ${EYEBROW} ${t.eyebrow}`}
              value={slide.eyebrow}
              onChange={onChange ? (v) => onChange({ eyebrow: v }) : undefined}
            />
            <EditableText
              as="h2"
              multiline
              className={`${H1} ${t.headline}`}
              value={slide.headline}
              onChange={onChange ? (v) => onChange({ headline: v }) : undefined}
            />
            {(slide.lede || onChange) && (
              <EditableText
                as="p"
                multiline
                className={`${LEDE} mt-3 ${t.lede}`}
                value={slide.lede ?? ''}
                onChange={onChange ? (v) => onChange({ lede: v }) : undefined}
                placeholder="Optional lede"
              />
            )}
          </header>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
            {slide.cards.map((card, i) => (
              <div key={i} className={CARD}>
                <EditableText
                  as="p"
                  className={`${CARD_TITLE} mb-1 text-[#2E3245]`}
                  value={card.title}
                  onChange={onChange ? (v) => updateCard(i, { title: v }) : undefined}
                />
                {(card.subtitle || onChange) && (
                  <EditableText
                    as="p"
                    className={`${ACCENT} mb-2 text-xs text-[#2E3245]/55`}
                    value={card.subtitle ?? ''}
                    onChange={onChange ? (v) => updateCard(i, { subtitle: v }) : undefined}
                    placeholder="Subtitle"
                  />
                )}
                <EditableText
                  as="p"
                  multiline
                  className={`${BODY_DIM} text-[#2E3245]/70`}
                  value={card.body}
                  onChange={onChange ? (v) => updateCard(i, { body: v }) : undefined}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </SlideFrame>
  );
}

// ── 5. QR (try it now) ─────────────────────────────────────────────────────
export function QrTemplate({ slide, onChange, onImageClick }: TemplateRenderProps<QrSlide>) {
  const t = textTone(slide.textColor ?? 'light');
  return (
    <SlideFrame id={slide.id} bg={slide.bg} bgColor={slide.bg?.color}>
      <ImageLayer onClick={onImageClick} />
      <div className={`relative z-10 flex min-h-screen items-center ${PAD} ${INNER}`}>
        <div className="grid w-full grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-16">
          <div className="max-w-xl">
            <EditableText
              as="p"
              className={`mb-3 ${EYEBROW} ${t.eyebrow}`}
              value={slide.eyebrow}
              onChange={onChange ? (v) => onChange({ eyebrow: v }) : undefined}
            />
            <EditableText
              as="h2"
              multiline
              className={`${H1} ${t.headline}`}
              value={slide.headline}
              onChange={onChange ? (v) => onChange({ headline: v }) : undefined}
            />
            {(slide.lede || onChange) && (
              <EditableText
                as="p"
                className={`${LEDE} mt-3 ${t.lede} ${ACCENT}`}
                value={slide.lede ?? ''}
                onChange={onChange ? (v) => onChange({ lede: v }) : undefined}
                placeholder="Phone or accent line"
              />
            )}
            {(slide.body || onChange) && (
              <EditableText
                as="p"
                multiline
                className={`${BODY} mt-6 ${t.body}`}
                value={slide.body ?? ''}
                onChange={onChange ? (v) => onChange({ body: v }) : undefined}
                placeholder="Body copy"
              />
            )}
          </div>
          <div className="flex justify-center lg:justify-end">
            <div className="rounded-3xl border border-[#2E3245]/10 bg-white p-6 md:p-8">
              <div className="mx-auto aspect-square w-[240px] md:w-[280px]">
                <QRCode
                  value={slide.qrValue || 'https://robin.guide'}
                  size={280}
                  level="M"
                  fgColor="#2E3245"
                  bgColor="#FFFFFF"
                  style={{ height: '100%', width: '100%' }}
                />
              </div>
              {(slide.qrCaption || onChange) && (
                <EditableText
                  as="p"
                  className={`${ACCENT} mt-5 text-center text-sm text-[#2E3245] md:text-base`}
                  value={slide.qrCaption ?? ''}
                  onChange={onChange ? (v) => onChange({ qrCaption: v }) : undefined}
                  placeholder="QR caption"
                />
              )}
              {(slide.qrSubCaption || onChange) && (
                <EditableText
                  as="p"
                  className={`${CAPTION} mt-1 text-center text-[#2E3245]/55`}
                  value={slide.qrSubCaption ?? ''}
                  onChange={onChange ? (v) => onChange({ qrSubCaption: v }) : undefined}
                  placeholder="QR sub-caption"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </SlideFrame>
  );
}

// ── 6. Problems (cards with bullets) ───────────────────────────────────────
export function ProblemsTemplate({
  slide,
  onChange,
  onImageClick,
}: TemplateRenderProps<ProblemsSlide>) {
  const t = textTone(slide.textColor ?? 'light');
  const alignRight = (slide.align ?? 'right') === 'right';
  const updateProblem = (i: number, patch: Partial<ProblemsSlide['problems'][number]>) => {
    if (!onChange) return;
    const next = slide.problems.map((p, j) => (i === j ? { ...p, ...patch } : p));
    onChange({ problems: next });
  };
  const updateBullet = (pi: number, bi: number, value: string) => {
    if (!onChange) return;
    const next = slide.problems.map((p, j) =>
      j === pi ? { ...p, bullets: p.bullets.map((b, k) => (k === bi ? value : b)) } : p,
    );
    onChange({ problems: next });
  };
  return (
    <SlideFrame id={slide.id} bg={slide.bg} bgColor={slide.bg?.color}>
      <ImageLayer onClick={onImageClick} />
      <div className={`relative z-10 flex min-h-screen flex-col justify-center ${PAD} ${INNER}`}>
        <div className={`${alignRight ? 'ml-auto' : ''} w-full max-w-full md:max-w-[66%]`}>
          <header className="mb-8 max-w-3xl">
            <EditableText
              as="p"
              className={`${EYEBROW} mb-3 ${t.eyebrow}`}
              value={slide.eyebrow}
              onChange={onChange ? (v) => onChange({ eyebrow: v }) : undefined}
            />
            <EditableText
              as="h2"
              multiline
              className={`${H1} ${t.headline}`}
              value={slide.headline}
              onChange={onChange ? (v) => onChange({ headline: v }) : undefined}
            />
          </header>
          <div className="grid w-full grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
            {slide.problems.map((p, pi) => (
              <div key={pi} className={`${CARD} md:p-7`}>
                <EditableText
                  as="p"
                  className={`${EYEBROW_SM} mb-2 text-[#2E3245]/50`}
                  value={p.tag}
                  onChange={onChange ? (v) => updateProblem(pi, { tag: v }) : undefined}
                />
                <EditableText
                  as="p"
                  multiline
                  className={`${H2} mb-3 text-[#2E3245]`}
                  value={p.title}
                  onChange={onChange ? (v) => updateProblem(pi, { title: v }) : undefined}
                />
                <EditableText
                  as="p"
                  multiline
                  className={`${BODY_DIM} mb-4 text-[#2E3245]/70`}
                  value={p.lede}
                  onChange={onChange ? (v) => updateProblem(pi, { lede: v }) : undefined}
                />
                <ul className={`${DECK_BULLET_LIST} text-[#2E3245]`}>
                  {p.bullets.map((b, bi) => (
                    <li key={bi} className={DECK_BULLET_ROW}>
                      <span className={DECK_BULLET_MARKER} aria-hidden>
                        {DECK_BULLET_GLYPH}
                      </span>
                      <EditableText
                        as="span"
                        multiline
                        className={`${DECK_BULLET_TEXT} ${BODY} flex-1 text-[#2E3245]/88`}
                        value={b}
                        onChange={onChange ? (v) => updateBullet(pi, bi, v) : undefined}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SlideFrame>
  );
}

// ── 7. Pricing ─────────────────────────────────────────────────────────────
export function PricingTemplate({
  slide,
  onChange,
  onImageClick,
}: TemplateRenderProps<PricingSlide>) {
  const t = textTone(slide.textColor ?? 'dark');
  const updateTier = (i: number, patch: Partial<PricingSlide['tiers'][number]>) => {
    if (!onChange) return;
    const next = slide.tiers.map((tier, j) => (i === j ? { ...tier, ...patch } : tier));
    onChange({ tiers: next });
  };
  const updateFeature = (ti: number, fi: number, value: string) => {
    if (!onChange) return;
    const next = slide.tiers.map((tier, j) =>
      j === ti ? { ...tier, features: tier.features.map((f, k) => (k === fi ? value : f)) } : tier,
    );
    onChange({ tiers: next });
  };
  return (
    <SlideFrame id={slide.id} bg={slide.bg} bgColor={slide.bg?.color}>
      <ImageLayer onClick={onImageClick} />
      <div className={`relative z-10 flex min-h-screen items-center ${PAD} ${INNER}`}>
        <div className="w-full max-w-[min(92%,65%)] space-y-8">
          <div>
            <EditableText
              as="p"
              className={`mb-3 ${EYEBROW} ${t.eyebrow}`}
              value={slide.eyebrow}
              onChange={onChange ? (v) => onChange({ eyebrow: v }) : undefined}
            />
            <EditableText
              as="h2"
              multiline
              className={`${H1} ${t.headline}`}
              value={slide.headline}
              onChange={onChange ? (v) => onChange({ headline: v }) : undefined}
            />
            {(slide.lede || onChange) && (
              <EditableText
                as="p"
                multiline
                className={`${LEDE} mt-3 ${t.lede}`}
                value={slide.lede ?? ''}
                onChange={onChange ? (v) => onChange({ lede: v }) : undefined}
                placeholder="Optional lede"
              />
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
            {slide.tiers.map((tier, i) => (
              <div
                key={i}
                className={`relative rounded-2xl bg-white p-5 md:p-6 ${
                  tier.highlighted ? 'border-2 border-marine-green' : 'border border-[#2E3245]/10'
                }`}
              >
                {tier.badge && (
                  <span
                    className={`${BADGE} absolute -top-2.5 left-5 rounded-full bg-marine-green px-2.5 py-0.5 text-white`}
                  >
                    {tier.badge}
                  </span>
                )}
                <EditableText
                  as="p"
                  className={`${ACCENT} text-[13px] tracking-tight text-[#2E3245] md:text-[14px]`}
                  value={tier.name}
                  onChange={onChange ? (v) => updateTier(i, { name: v }) : undefined}
                />
                <div className="mt-2 flex items-baseline gap-1.5">
                  <EditableText
                    as="span"
                    className={`${PRICE} text-[#2E3245]`}
                    value={tier.price}
                    onChange={onChange ? (v) => updateTier(i, { price: v }) : undefined}
                  />
                  {(tier.priceSuffix || onChange) && (
                    <EditableText
                      as="span"
                      className={`${ACCENT} text-[11px] text-[#2E3245]/45`}
                      value={tier.priceSuffix ?? ''}
                      onChange={onChange ? (v) => updateTier(i, { priceSuffix: v }) : undefined}
                      placeholder="CAD · one event"
                    />
                  )}
                </div>
                <EditableText
                  as="p"
                  multiline
                  className={`${BODY_SM} mt-4 text-[#2E3245]/85`}
                  value={tier.body}
                  onChange={onChange ? (v) => updateTier(i, { body: v }) : undefined}
                />
                <ul className={`mt-4 ${DECK_BULLET_LIST_TIGHT} text-[#2E3245]`}>
                  {tier.features.map((f, fi) => (
                    <li key={fi} className={DECK_BULLET_ROW}>
                      <span className={`${DECK_BULLET_MARKER} text-[11px] pt-[0.32em]`} aria-hidden>
                        {DECK_BULLET_GLYPH}
                      </span>
                      <EditableText
                        as="span"
                        multiline
                        className={`${DECK_BULLET_TEXT} flex-1 text-sm leading-[1.5] text-[#2E3245]/78`}
                        value={f}
                        onChange={onChange ? (v) => updateFeature(i, fi, v) : undefined}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          {(slide.footer || onChange) && (
            <EditableText
              as="p"
              className={`${QUOTE_SM} text-marine-green`}
              value={slide.footer ?? ''}
              onChange={onChange ? (v) => onChange({ footer: v }) : undefined}
              placeholder="Closing line"
            />
          )}
        </div>
      </div>
    </SlideFrame>
  );
}

// ── 8. Two-column (who does what) ──────────────────────────────────────────
export function TwoColumnTemplate({
  slide,
  onChange,
  onImageClick,
}: TemplateRenderProps<TwoColumnSlide>) {
  const t = textTone(slide.textColor ?? 'dark');
  const showMiddle = slide.showMiddleGap ?? true;
  const updateColumn = (i: number, patch: Partial<TwoColumnSlide['columns'][number]>) => {
    if (!onChange) return;
    const next = slide.columns.map((c, j) => (i === j ? { ...c, ...patch } : c));
    onChange({ columns: next });
  };
  const updateBullet = (ci: number, bi: number, value: string) => {
    if (!onChange) return;
    const next = slide.columns.map((c, j) =>
      j === ci ? { ...c, bullets: c.bullets.map((b, k) => (k === bi ? value : b)) } : c,
    );
    onChange({ columns: next });
  };
  const left = slide.columns[0];
  const right = slide.columns[1];
  return (
    <SlideFrame id={slide.id} bg={slide.bg} bgColor={slide.bg?.color}>
      <ImageLayer onClick={onImageClick} />
      <div
        className={`relative z-10 flex min-h-screen flex-col justify-start pt-12 sm:pt-14 md:pt-16 ${PAD} ${INNER}`}
      >
        <header className="mb-6 max-w-3xl">
          <EditableText
            as="p"
            className={`${EYEBROW} mb-3 ${t.eyebrow}`}
            value={slide.eyebrow}
            onChange={onChange ? (v) => onChange({ eyebrow: v }) : undefined}
          />
          <EditableText
            as="h2"
            multiline
            className={`${H1} ${t.headline}`}
            value={slide.headline}
            onChange={onChange ? (v) => onChange({ headline: v }) : undefined}
          />
          {(slide.lede || onChange) && (
            <EditableText
              as="p"
              multiline
              className={`${LEDE} mt-3 ${t.lede}`}
              value={slide.lede ?? ''}
              onChange={onChange ? (v) => onChange({ lede: v }) : undefined}
              placeholder="Optional lede"
            />
          )}
        </header>
        <div
          className={`grid grid-cols-1 gap-5 lg:gap-6 ${
            showMiddle
              ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,0.6fr)_minmax(0,1fr)]'
              : 'lg:grid-cols-2'
          }`}
        >
          {left && (
            <div className={CARD}>
              <EditableText
                as="p"
                className={`${CARD_TITLE} mb-3 text-[#2E3245]`}
                value={left.title}
                onChange={onChange ? (v) => updateColumn(0, { title: v }) : undefined}
              />
              <ul className={`${DECK_BULLET_LIST} text-[#2E3245]`}>
                {left.bullets.map((b, bi) => (
                  <li key={bi} className={DECK_BULLET_ROW}>
                    <span className={DECK_BULLET_MARKER} aria-hidden>
                      {DECK_BULLET_GLYPH}
                    </span>
                    <EditableText
                      as="span"
                      multiline
                      className={`${DECK_BULLET_TEXT} ${BODY} flex-1 text-[#2E3245]/88`}
                      value={b}
                      onChange={onChange ? (v) => updateBullet(0, bi, v) : undefined}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {showMiddle && <div aria-hidden className="hidden lg:block" />}
          {right && (
            <div className={CARD}>
              <EditableText
                as="p"
                className={`${CARD_TITLE} mb-3 text-[#2E3245]`}
                value={right.title}
                onChange={onChange ? (v) => updateColumn(1, { title: v }) : undefined}
              />
              <ul className={`${DECK_BULLET_LIST} text-[#2E3245]`}>
                {right.bullets.map((b, bi) => (
                  <li key={bi} className={DECK_BULLET_ROW}>
                    <span className={DECK_BULLET_MARKER} aria-hidden>
                      {DECK_BULLET_GLYPH}
                    </span>
                    <EditableText
                      as="span"
                      multiline
                      className={`${DECK_BULLET_TEXT} ${BODY} flex-1 text-[#2E3245]/88`}
                      value={b}
                      onChange={onChange ? (v) => updateBullet(1, bi, v) : undefined}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </SlideFrame>
  );
}

// ── 9. Ask (CTA) ───────────────────────────────────────────────────────────
export function AskTemplate({ slide, onChange, onImageClick }: TemplateRenderProps<AskSlide>) {
  const t = textTone(slide.textColor ?? 'light');
  const alignRight = slide.align === 'right';
  return (
    <SlideFrame id={slide.id} bg={slide.bg} bgColor={slide.bg?.color}>
      <ImageLayer onClick={onImageClick} />
      <div
        className={`relative z-10 flex min-h-screen w-full flex-col justify-center ${PAD} ${INNER}`}
      >
        <div
          className={`${alignRight ? 'ml-auto' : 'mr-auto'} max-w-2xl space-y-8 ${alignRight ? 'text-right' : 'text-left'}`}
        >
          <header>
            <EditableText
              as="p"
              className={`${EYEBROW} mb-3 ${t.eyebrow}`}
              value={slide.eyebrow}
              onChange={onChange ? (v) => onChange({ eyebrow: v }) : undefined}
            />
            <EditableText
              as="h2"
              multiline
              className={`${H1} mb-5 ${t.headline}`}
              value={slide.headline}
              onChange={onChange ? (v) => onChange({ headline: v }) : undefined}
            />
            {(slide.lede || onChange) && (
              <EditableText
                as="p"
                multiline
                className={`${BODY_LG} max-w-xl ${t.lede}`}
                value={slide.lede ?? ''}
                onChange={onChange ? (v) => onChange({ lede: v }) : undefined}
                placeholder="Optional lede"
              />
            )}
          </header>
          {(slide.contact || onChange) && (
            <EditableText
              as="p"
              className={`${BODY_SM} pt-2 ${t.body}`}
              value={slide.contact ?? ''}
              onChange={onChange ? (v) => onChange({ contact: v }) : undefined}
              placeholder="domain.com · email@domain.com"
            />
          )}
        </div>
      </div>
    </SlideFrame>
  );
}

// ── Dispatcher ─────────────────────────────────────────────────────────────
interface RenderSlideProps {
  slide: Slide;
  onChange?: (slide: Slide) => void;
  onImageClick?: () => void;
}

export function RenderSlide({ slide, onChange, onImageClick }: RenderSlideProps) {
  const merge = <T extends Slide>(patch: Partial<T>) => {
    if (!onChange) return;
    onChange({ ...slide, ...patch } as Slide);
  };
  switch (slide.type) {
    case 'cover':
      return (
        <CoverTemplate
          slide={slide}
          onChange={onChange ? (p) => merge<CoverSlide>(p) : undefined}
          onImageClick={onImageClick}
        />
      );
    case 'right-column':
      return (
        <RightColumnTemplate
          slide={slide}
          onChange={onChange ? (p) => merge<RightColumnSlide>(p) : undefined}
          onImageClick={onImageClick}
        />
      );
    case 'transcript':
      return (
        <TranscriptTemplate
          slide={slide}
          onChange={onChange ? (p) => merge<TranscriptSlide>(p) : undefined}
          onImageClick={onImageClick}
        />
      );
    case 'cards':
      return (
        <CardsTemplate
          slide={slide}
          onChange={onChange ? (p) => merge<CardsSlide>(p) : undefined}
          onImageClick={onImageClick}
        />
      );
    case 'qr':
      return (
        <QrTemplate
          slide={slide}
          onChange={onChange ? (p) => merge<QrSlide>(p) : undefined}
          onImageClick={onImageClick}
        />
      );
    case 'problems':
      return (
        <ProblemsTemplate
          slide={slide}
          onChange={onChange ? (p) => merge<ProblemsSlide>(p) : undefined}
          onImageClick={onImageClick}
        />
      );
    case 'pricing':
      return (
        <PricingTemplate
          slide={slide}
          onChange={onChange ? (p) => merge<PricingSlide>(p) : undefined}
          onImageClick={onImageClick}
        />
      );
    case 'two-column':
      return (
        <TwoColumnTemplate
          slide={slide}
          onChange={onChange ? (p) => merge<TwoColumnSlide>(p) : undefined}
          onImageClick={onImageClick}
        />
      );
    case 'ask':
      return (
        <AskTemplate
          slide={slide}
          onChange={onChange ? (p) => merge<AskSlide>(p) : undefined}
          onImageClick={onImageClick}
        />
      );
    case 'blocks': {
      const bslide = slide as BlockSlide;
      return (
        <BlockSlideView
          slide={bslide}
          onTextChange={
            onChange
              ? (blockId, text) => {
                  onChange(patchSlideBlock(bslide, blockId, { text } as Partial<Block>) as Slide);
                }
              : undefined
          }
          onQrChange={
            onChange
              ? (blockId, patch) => {
                  onChange(patchSlideBlock(bslide, blockId, patch as Partial<Block>) as Slide);
                }
              : undefined
          }
        />
      );
    }
  }
}
